import Dropdown from "@/lib/Dropdown";
import useOperationContext from "./useOperationContext";
import useGetCurrentQuoteRevision from "@/lib/SelectedQuoteRevisionIdContext/useGetCurrentQuoteRevision";
import useGetTimeCostPreset from "@/lib/queries/useGetTimeCostPreset";
import { components } from "@/lib/ApiSchema";
import { useEffect, useState } from "react";
import Button from "@/lib/Button";
import useUpdateTimeCostPreset from "@/lib/queries/useUpdateTimeCostPreset";
import ClearIcon from "~icons/fa6-solid/x";
import { unitLabel, unitOptions } from "./unit";
import getValidationErrors from "./getValidationErrors";
import ValidationErrorsTooltip from "./ValidationErrorsTooltip";
import classnames from "classnames";
import CaretDownIcon from "@/lib/CaretDownIcon";
import { setIn, getIn } from "@/lib/utils";

export default function SelectNamedTimeCost({
  label,
  keyPath,
}: {
  label: string;
  keyPath: readonly PropertyKey[];
}) {
  const tooltipId = Math.random().toString();

  const updateTimeCostPreset = useUpdateTimeCostPreset();

  const canEditQuoteRevision = useGetCurrentQuoteRevision()?.can_edit;

  const { operationData: data, updateOperation } = useOperationContext();
  const { id, draft, augmented, validation_errors } = data;

  const draftNamedTimeCost = getIn(draft, keyPath, null) as
    | components["schemas"]["NamedTimeCost"]
    | null;
  const augmentedNamedTimeCost = getIn(
    augmented,
    keyPath,
    null,
  ) as components["schemas"]["NamedTimeCost"];
  const { name: selectedName, cost: selectedCost } =
    draftNamedTimeCost ?? augmentedNamedTimeCost;
  const { dunit, nunit, value } = selectedCost;

  const [editTimeCostPreset, setEditTimeCostPreset] = useState<
    components["schemas"]["TimeCostPresetSchema"][]
  >([]);

  const timeCostPreset = useGetTimeCostPreset();
  useEffect(() => {
    setEditTimeCostPreset(timeCostPreset ?? []);
  }, [timeCostPreset]);
  const [editing, setEditing] = useState(false);

  const inputValid = editTimeCostPreset.map(
    ({ name, currency, cost, duration_unit }) => ({
      name: name.length > 0,
      currency: currency === "USD",
      cost: typeof cost === "number" || !Number.isNaN(parseFloat(cost)),
      duration_unit: !!unitLabel("time", duration_unit),
    }),
  );
  const anyInputError = inputValid.reduce(
    (error, input) => error || Object.values(input).includes(false),
    false,
  );

  const errorMessages = getValidationErrors(validation_errors, keyPath);

  return (
    <>
      <div
        data-tooltip-id={tooltipId}
        className={`w-40 h-[30px] pb-1 mt-1 flex flex-col justify-center items-start text-sm text-gray-800 rounded-sm ${errorMessages.length ? "border border-red-600" : ""}`}
      >
        <div className="px-1 text-xs text-gray-400 mt-[-8px] bg-white">
          {label}
        </div>
        <div className="w-full">
          <Dropdown
            disabled={!canEditQuoteRevision}
            placement="bottom-start"
            trigger={({ expanded }: { expanded: boolean }) => (
              <div className="w-full px-1 flex gap-2 justify-between cursor-pointer">
                <div
                  className={`${draftNamedTimeCost && "text-blue-600"} grow truncate`}
                >
                  {displayCurrency(nunit)}
                  {value}/{dunit} &nbsp;{selectedName}
                </div>
                <CaretDownIcon expanded={expanded} />
              </div>
            )}
          >
            <div
              className="bg-white p-2 rounded-lg"
              onClick={(e) => {
                if (editing) {
                  e.stopPropagation();
                }
              }}
            >
              <table>
                <tbody>
                  {editing
                    ? editTimeCostPreset.map(
                        ({ name, cost, duration_unit }, i) => {
                          const isDefault = name.toLowerCase() === "default";
                          return (
                            <tr key={i}>
                              <td className="p-2 w-40 flex gap-1">
                                $
                                <input
                                  className={`w-18 ${inputClassName(inputValid[i].cost)}`}
                                  value={cost}
                                  onChange={(e) => {
                                    setEditTimeCostPreset(
                                      editTimeCostPreset.map(
                                        (preset, index) => {
                                          if (index === i) {
                                            return {
                                              ...preset,
                                              cost: e.target.value,
                                            };
                                          }
                                          return preset;
                                        },
                                      ),
                                    );
                                  }}
                                />
                                /
                                {isDefault ? (
                                  <span>{duration_unit}</span>
                                ) : (
                                  <input
                                    className={`w-8 ${inputClassName(inputValid[i].duration_unit)}`}
                                    value={duration_unit}
                                    onChange={(e) => {
                                      setEditTimeCostPreset(
                                        editTimeCostPreset.map(
                                          (preset, index) => {
                                            if (index === i) {
                                              return {
                                                ...preset,
                                                duration_unit: e.target
                                                  .value as components["schemas"]["TimeCostPresetSchema"]["duration_unit"],
                                              };
                                            }
                                            return preset;
                                          },
                                        ),
                                      );
                                    }}
                                  />
                                )}
                              </td>
                              <td className="p-2">
                                <div className="flex gap-2 items-center">
                                  {isDefault ? (
                                    <span>{name}</span>
                                  ) : (
                                    <input
                                      className={`${inputClassName(inputValid[i].name)}`}
                                      value={name}
                                      onChange={(e) => {
                                        if (
                                          e.target.value.toLowerCase() ===
                                          "default"
                                        ) {
                                          return;
                                        }

                                        setEditTimeCostPreset(
                                          editTimeCostPreset.map(
                                            (preset, index) => {
                                              if (index === i) {
                                                return {
                                                  ...preset,
                                                  name: e.target.value,
                                                };
                                              }
                                              return preset;
                                            },
                                          ),
                                        );
                                      }}
                                    />
                                  )}
                                  {!isDefault && (
                                    <ClearIcon
                                      className="cursor-pointer text-slate-400 hover:text-slate-600 size-3"
                                      onClick={() => {
                                        setEditTimeCostPreset(
                                          editTimeCostPreset.filter(
                                            (_, k) => k !== i,
                                          ),
                                        );
                                      }}
                                    />
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        },
                      )
                    : (timeCostPreset ?? []).map(
                        ({ name, cost, currency, duration_unit }, i) => (
                          <tr
                            key={i}
                            className={`hover:bg-slate-100 cursor-pointer ${name === selectedName ? "bg-slate-100" : ""}`}
                            onClick={() => {
                              updateOperation(
                                id,
                                setIn(draft, keyPath, {
                                  name,
                                  cost: {
                                    value: cost,
                                    nunit: currency,
                                    dunit: duration_unit,
                                  },
                                }),
                              );
                            }}
                          >
                            <td className="p-2 w-40">
                              {displayCurrency(currency)}
                              {cost}/{duration_unit}
                            </td>
                            <td className="p-2">{name}</td>
                          </tr>
                        ),
                      )}
                  {editing && (
                    <tr>
                      <td colSpan={2}>
                        <div className="text-slate-400 text-xs p-2">
                          <p>- Cost must be a valid number.</p>
                          <p>
                            - Time unit must be one of{" "}
                            {unitOptions.time
                              .filter(
                                ({ value }) =>
                                  !["s", "month", "year"].includes(value),
                              )
                              .map(({ value }) => `"${value}"`)
                              .join(", ")}
                            .
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                  <tr>
                    <td>
                      {editing ? (
                        <Button
                          className={buttonClassName(true)}
                          onClick={() =>
                            setEditTimeCostPreset([
                              ...editTimeCostPreset,
                              {
                                name: "",
                                currency: "USD",
                                duration_unit: "h",
                                cost: "",
                              },
                            ])
                          }
                        >
                          Add
                        </Button>
                      ) : (
                        <Button
                          className={classnames(buttonClassName(true), "mt-2")}
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditing(true);
                          }}
                        >
                          Edit
                        </Button>
                      )}
                    </td>
                    <td className="flex gap-2 justify-end">
                      {editing && (
                        <>
                          <Button
                            className={buttonClassName(false)}
                            onClick={() => {
                              setEditTimeCostPreset(timeCostPreset ?? []);
                              setEditing(false);
                            }}
                          >
                            Cancel
                          </Button>
                          <Button
                            disabled={anyInputError}
                            className={buttonClassName(true)}
                            onClick={() => {
                              updateTimeCostPreset(editTimeCostPreset);
                              setEditing(false);
                            }}
                          >
                            Save
                          </Button>
                        </>
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </Dropdown>
        </div>
      </div>
      <ValidationErrorsTooltip
        tooltipId={tooltipId}
        errorMessages={errorMessages}
        showTooltip={errorMessages.length > 0}
      />
    </>
  );
}

function displayCurrency(currency: string) {
  switch (currency) {
    case "USD":
      return "$";
    default:
      return "";
  }
}

function inputClassName(valid: boolean) {
  return `px-1 outline rounded-sm ${valid ? "outline-slate-400" : "outline-red-500"}`;
}
function buttonClassName(blue: boolean) {
  return `text-xs ${blue && "text-blue-500"}`;
}
